"use client";

import { useEffect } from "react";
import { reportClientException } from "@/lib/analytics/report-client-exception";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Top-level fallback for errors that escape the root layout — i.e. the
 * (site) `error.tsx` could not render either. Next.js requires this file
 * to render its own <html>/<body>.
 *
 * Should be rare. When it does fire, we still want PostHog to know.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    reportClientException({
      error,
      source: "global_error_boundary",
      extra: { digest: error.digest },
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "system-ui, -apple-system, sans-serif",
          margin: 0,
          padding: "48px 16px",
          textAlign: "center",
          backgroundColor: "#fff",
          color: "#111",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: 12 }}>
          Something went wrong
        </h1>
        <p style={{ color: "#666", marginBottom: 24 }}>
          Reload the page or come back in a moment.
        </p>
        <button
          onClick={() => reset()}
          style={{
            backgroundColor: "#facc15",
            border: "none",
            padding: "10px 20px",
            borderRadius: 999,
            fontWeight: 600,
            cursor: "pointer",
          }}
          type="button"
        >
          Reload
        </button>
        {error.digest && (
          <p style={{ color: "#999", fontSize: 12, marginTop: 24 }}>
            Reference: <code>{error.digest}</code>
          </p>
        )}
      </body>
    </html>
  );
}
